import { isEmpty, isNumber } from 'lodash';
import React, { FunctionComponent, createContext, useContext, useRef } from 'react';

interface configurationInterface<T> {
    key: string;
    validate: (value: T) => boolean;
    message: string;
}

export const validateRequired = (value: string | number, zeroIsAllowed: boolean = true) => {
    if (!zeroIsAllowed && +value === 0) return true;
    else if (zeroIsAllowed && isNumber(value)) return false;
    else return isEmpty(value);
};

export const validateEmail = (value: string) => {
    //pattern derived from stringHelper cs
    return !/^(?:[a-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+\/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)$/i.test(
        value
    );
};

export const validatePassword = (value: string) => {
    //pattern derived from stringHelper cs
    return !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d_\-\+\\=\/@\.!#$%&\'\*\+\;\[\]\,\(\)\{\}\^\°\~\`\:]{8,}$/.test(value);
};

export const validateUrl = (value: string): boolean => {
    if (!value) return true;
    const protocolRegex = /^(https?:\/\/)/;
    const urlRegex =
        /^https?:\/\/((([a-z\d]([a-z\d-]*[a-z\d])*)\.)+[a-z]{2,}|((\d{1,3}\.){3}\d{1,3}))(:\d+)?(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=-]*)?(\#[-a-z\d_]*)?$/i;

    return !(protocolRegex.test(value) || urlRegex.test(value));
};

export const isStepAligned = (value: number, step: number): boolean => {
    if (isNumber(value) && isNumber(step) && step > 0) {
        const epsilon = 1e-10;
        const nearest = Math.round(value / step) * step;
        return Math.abs(nearest - value) < epsilon;
    }
    return true;
};

export function useValidation<T>(configurations: configurationInterface<T>[]) {
    const _validationContext = useContext(validationContext);

    const clearValidation = () => {
        if (_validationContext.update && configurations.length > 0) _validationContext.update(configurations[0].key, undefined);
    };

    return {
        check: (value: T) => {
            // Clear validation
            clearValidation();
            const validations = configurations.filter((configuration) => {
                if (configuration.validate) return configuration.validate(value);
                return false;
            });

            if (_validationContext.update && validations.length > 0) _validationContext.update(validations[0].key, validations[0].message);
        },
        remove: () => {
            if (_validationContext.remove && configurations.length > 0) _validationContext.remove(configurations[0].key);
        }
    };
}

interface ValidationContextInterface {
    update: (validationKey: string, message: string | null | undefined) => void;
    remove: (validationKey: string) => void;
    clear: () => void;
    isValid: () => boolean;
    validations: { validationKey: string; message: string | null | undefined }[];
}

export const validationContext = createContext<ValidationContextInterface>({
    update: (validationKey: string, message: string | null | undefined) => {},
    remove: (validationKey: string) => {},
    clear: () => {},
    isValid: () => true,
    validations: []
});

export const useValidationContext = () => useContext(validationContext);

export const ValidationStateProvider: FunctionComponent<{ children?: React.ReactNode }> = ({ children }) => {
    const validationState = useRef<ValidationContextInterface>({
        update: (validationKey: string, message: string | null | undefined) => {
            // i componenti figli aggiornano il contesto
            if (validationState.current.validations) {
                const validations = [
                    ...validationState.current.validations.filter((v) => v.validationKey != validationKey),
                    { validationKey: validationKey, message: message }
                ];
                validationState.current.validations = validations;
            }
        },
        // Allow to remove all validations with a specific prefix
        remove: (validationKeyPrefix: string) => {
            if (validationState.current.validations) {
                const validations = [...validationState.current.validations.filter((v) => !v.validationKey.startsWith(validationKeyPrefix))];
                validationState.current.validations = validations;
            }
        },
        clear: () => {
            validationState.current.validations = [];
        },
        isValid: (): boolean => {
            console.log('isValid', validationState.current.validations);
            return !validationState.current.validations.some((x) => x.message);
        },
        validations: []
    });

    return <validationContext.Provider value={validationState.current}> {children} </validationContext.Provider>;
};
